import { infantSelectors } from '@/store/infant';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  startOfWeek,
  startOfDay,
  endOfDay,
  addDays,
  isWithinInterval,
  isBefore,
  isValid,
} from 'date-fns';
import { VisitDto } from '@ecdlink/core';
import { motherSelectors } from '@/store/mother';

export const useVisitsDueThisWeek = () => {
  const infants = useSelector(infantSelectors.getInfants);
  const mothers = useSelector(motherSelectors.getMothers);

  return useMemo(() => {
    const today = startOfDay(new Date());

    // This week: Monday 00:00 → Sunday 23:59:59
    const weekStart = startOfDay(startOfWeek(today, { weekStartsOn: 1 }));
    const weekEnd = endOfDay(addDays(weekStart, 6));

    const isThisWeek = (date: Date): boolean =>
      isWithinInterval(date, { start: weekStart, end: weekEnd });

    const parseDate = (dateStr: string | null | undefined): Date | null => {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      return isValid(date) ? startOfDay(date) : null;
    };

    const isVisitDueAndNotAttended = (visit: VisitDto): boolean => {
      if (visit.attended === true) return false;

      const plannedDate = parseDate(visit.plannedVisitDate);
      const dueDate = parseDate(visit.dueDate);
      const insertedDate = parseDate(visit.insertedDate);

      if (
        !plannedDate ||
        !insertedDate ||
        isBefore(plannedDate, insertedDate)
      ) {
        return false;
      }

      if (!isThisWeek(plannedDate)) {
        return false;
      }

      const effectiveDate = dueDate ?? plannedDate;
      return (
        isBefore(effectiveDate, today) ||
        effectiveDate.getTime() === today.getTime()
      );
    };

    // ────────────────────────────────────────────────
    //  Only keep infants/mothers that have ≥ 1 due visit this week
    // ────────────────────────────────────────────────
    const infantsWithDueVisits = (infants ?? []).filter((infant) => {
      const hasDueVisit = (infant.visits ?? []).some(isVisitDueAndNotAttended);
      return hasDueVisit;
    });

    const mothersWithDueVisits = (mothers ?? []).filter((mother) => {
      const hasDueVisit = (mother.visits ?? []).some(isVisitDueAndNotAttended);
      return hasDueVisit;
    });

    // Optional: sort infants alphabetically (or by any other criteria)
    infantsWithDueVisits.sort((a, b) => {
      const nameA = `${a?.user?.firstName ?? ''} ${a?.user?.surname ?? ''}`
        .trim()
        .toLowerCase();
      const nameB = `${b?.user?.firstName ?? ''} ${b?.user?.surname ?? ''}`
        .trim()
        .toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return {
      infantsDueThisWeek: infantsWithDueVisits,
      mothersDueThisWeek: mothersWithDueVisits,

      // For debugging / display in UI
      weekRange: {
        start: weekStart.toISOString().split('T')[0],
        end: weekEnd.toISOString().split('T')[0],
      },
    };
  }, [infants]);
};
